import { isActivePointsLotMovement } from "./pointMovementUi";

const MS_PER_DAY = 86400000;

export const EXPIRY_COLORS = {
    none: "#64748b",
    ok: "#059669",
    warning: "#D97706",
    critical: "#DC2626",
};

export function parseValidityDate(value) {
    if (!value) return null;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
}

function startOfDay(date) {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
}

/** Jours restants (0 = aujourd'hui, négatif = expiré). */
export function daysUntilExpiry(value) {
    const target = parseValidityDate(value);
    if (!target) return null;
    const today = startOfDay(new Date());
    return Math.ceil((startOfDay(target) - today) / MS_PER_DAY);
}

/**
 * Couleur selon l'urgence : vert (OK), orange (< 30 j), rouge (< 7 j ou expiré).
 */
export function getExpiryColor(value, { warningDays = 30, criticalDays = 7 } = {}) {
    if (!value) return EXPIRY_COLORS.none;
    const days = daysUntilExpiry(value);
    if (days == null) return EXPIRY_COLORS.none;
    if (days < 0 || days <= criticalDays) return EXPIRY_COLORS.critical;
    if (days <= warningDays) return EXPIRY_COLORS.warning;
    return EXPIRY_COLORS.ok;
}

export function formatValidityDate(value) {
    const d = parseValidityDate(value);
    return d ? d.toLocaleDateString("fr-FR") : "–";
}

function collectActiveLotsFromMovements(movements = []) {
    const today = startOfDay(new Date());
    const lotsByDate = new Map();

    (Array.isArray(movements) ? movements : []).forEach((m) => {
        if (!isActivePointsLotMovement(m) || !m?.expires_at) return;
        const key = String(m.expires_at).slice(0, 10);
        lotsByDate.set(key, (lotsByDate.get(key) || 0) + Number(m.points));
    });

    return [...lotsByDate.entries()]
        .map(([dateKey, points]) => ({
            dateKey,
            date: parseValidityDate(dateKey),
            points,
        }))
        .filter((lot) => lot.date && startOfDay(lot.date) >= today)
        .sort((a, b) => a.date - b.date);
}

function lotsHint(lots) {
    if (lots.length > 1) {
        return `Prochaine échéance · ${lots.length} lots de points avec des dates différentes`;
    }
    if (lots.length === 1) {
        const nearest = lots[0];
        return `${nearest.points} pt${nearest.points > 1 ? "s" : ""} concerné${nearest.points > 1 ? "s" : ""} à cette date`;
    }
    return null;
}

/**
 * Résumé « Points valables jusqu'au ».
 *
 * @param {object[]} movements
 * @param {string|null|undefined} fallbackDate — fiche client (legacy)
 * @param {string|null|undefined} loyaltyPointsExpiresAt — si défini (y compris null), priorité /loyalty
 */
export function summarizePointsValidity(
    movements = [],
    fallbackDate = null,
    loyaltyPointsExpiresAt = undefined,
) {
    const lotsFromMovements = collectActiveLotsFromMovements(movements);

    if (loyaltyPointsExpiresAt !== undefined) {
        const parsed = parseValidityDate(loyaltyPointsExpiresAt);
        if (parsed) {
            return {
                displayDate: parsed,
                lots: lotsFromMovements,
                multiple: lotsFromMovements.length > 1,
                hint: lotsHint(lotsFromMovements),
                source: "loyalty",
            };
        }
        return {
            displayDate: null,
            lots: [],
            multiple: false,
            hint: "Aucun point en cours de validité",
            source: "loyalty_none",
        };
    }

    if (lotsFromMovements.length > 0) {
        const nearest = lotsFromMovements[0];
        return {
            displayDate: nearest.date,
            lots: lotsFromMovements,
            multiple: lotsFromMovements.length > 1,
            hint: lotsHint(lotsFromMovements),
            source: "movements",
        };
    }

    const fallback = parseValidityDate(fallbackDate);
    if (fallback && startOfDay(fallback) >= startOfDay(new Date())) {
        return {
            displayDate: fallback,
            lots: [],
            multiple: false,
            hint: "Date indicative (aucune échéance détaillée dans l'historique des mouvements)",
            source: "fallback",
        };
    }

    return {
        displayDate: null,
        lots: [],
        multiple: false,
        hint: fallback
            ? "Aucun point en cours de validité"
            : "Aucune date de validité enregistrée",
        source: "none",
    };
}

/** /loyalty expose pointsExpiresAt — utiliser comme source de vérité si présent. */
export function resolveLoyaltyPointsExpiresAt(loyalty) {
    if (!loyalty || typeof loyalty !== "object") return undefined;
    if (Object.prototype.hasOwnProperty.call(loyalty, "pointsExpiresAt")) {
        return loyalty.pointsExpiresAt;
    }
    if (Object.prototype.hasOwnProperty.call(loyalty, "points_expires_at")) {
        return loyalty.points_expires_at;
    }
    return undefined;
}
